import { type ReactElement } from "react";

import { cn } from "~/lib/utils";
import { Skeleton } from "../ui/skeleton";

type ChatLoadingPhase = "workspace" | "history";
type ChatLoadingRow = {
  readonly id: string;
  readonly role: "assistant" | "user";
  readonly widths: readonly string[];
};

const WORKSPACE_ROWS: readonly ChatLoadingRow[] = [
  { id: "assistant-opening", role: "assistant", widths: ["w-11/12", "w-8/12", "w-5/12"] },
  { id: "user", role: "user", widths: ["w-10/12", "w-7/12"] },
  { id: "assistant-follow-up", role: "assistant", widths: ["w-full", "w-9/12"] },
];

const HISTORY_ROWS: readonly ChatLoadingRow[] = [
  { id: "assistant-history", role: "assistant", widths: ["w-10/12", "w-6/12"] },
  { id: "user-history", role: "user", widths: ["w-9/12"] },
  { id: "assistant-latest", role: "assistant", widths: ["w-11/12", "w-7/12"] },
];

interface ChatLoadingSkeletonProps {
  readonly phase: ChatLoadingPhase;
}

function ChatMessageSkeleton(props: { readonly row: ChatLoadingRow }): ReactElement {
  const isUser = props.row.role === "user";

  return (
    <div
      aria-hidden="true"
      className={cn(
        "space-y-2.5",
        isUser
          ? "ml-auto w-[68%] rounded-[18px] border border-border/60 bg-muted/35 px-4 py-3.5"
          : "w-full max-w-[88%] py-1",
      )}
      data-chat-message-skeleton="true"
      data-chat-message-skeleton-role={props.row.role}
    >
      {props.row.widths.map((width) => (
        <Skeleton
          key={`${props.row.id}-${width}`}
          className={cn(
            "motion-reduce:animate-none",
            isUser ? "h-3.5 rounded-full" : "h-3 rounded-full",
            width,
          )}
        />
      ))}
    </div>
  );
}

function ChatComposerSkeleton(): ReactElement {
  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full min-w-0 max-w-3xl rounded-[22px] border border-border bg-card px-4 py-3.5"
      data-chat-composer-skeleton="true"
    >
      <Skeleton className="h-3.5 w-7/12 rounded-full motion-reduce:animate-none" />
      <div className="mt-7 flex items-center gap-2">
        <Skeleton className="h-7 w-24 rounded-md motion-reduce:animate-none" />
        <Skeleton className="h-7 w-20 rounded-md motion-reduce:animate-none" />
        <Skeleton className="ml-auto size-8 rounded-full motion-reduce:animate-none" />
      </div>
    </div>
  );
}

export function ChatLoadingSkeleton(props: ChatLoadingSkeletonProps): ReactElement {
  const rows = props.phase === "workspace" ? WORKSPACE_ROWS : HISTORY_ROWS;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-chat-loading-skeleton={props.phase}>
      <div className="min-h-0 flex-1 overflow-hidden">
        <div
          className={cn(
            "mx-auto w-full max-w-3xl px-5 sm:px-6",
            props.phase === "workspace" ? "space-y-8 py-8" : "space-y-6 py-6",
          )}
        >
          {rows.map((row) => (
            <ChatMessageSkeleton key={row.id} row={row} />
          ))}
        </div>
      </div>
      <div className="shrink-0 px-3 pt-1.5 pb-3 sm:px-5 sm:pt-2 sm:pb-4">
        <ChatComposerSkeleton />
      </div>
    </div>
  );
}
